import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Hand,
  Loader2,
  MousePointerClick,
  Plus,
  Trash2,
  Upload,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { API_BASE, apiRequest } from '../utils/api';
import { useAuth } from '../components/auth/AuthComponents';

const FIELD_TYPES = [
  { id: 'signature', label: 'Signature' },
  { id: 'initial', label: 'Initial' },
  { id: 'date', label: 'Date' },
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'text', label: 'Text' },
  { id: 'checkbox', label: 'Checkbox' },
];

const STEPS = ['Document', 'Signers', 'Fields', 'Review & send'];

const getResumeStep = (req) => {
  if (!req?.resolved_pdf_url) return 1;
  const hasSigners = (req.signers || []).some((s) => s.name?.trim() && s.email?.trim());
  if (!hasSigners) return 2;
  if (!(req.fields || []).length) return 3;
  return 4;
};

const emptySigner = () => ({
  name: '',
  email: '',
  role: 'signer',
  signing_order: 0,
  is_mandatory: true,
  access_code: '',
  private_message: '',
});

const fieldKey = (field, index) => String(field.id ?? field.tempId ?? index);

const showsUnderscore = (type) =>
  ['text', 'name', 'email', 'date', 'initial', 'signature'].includes(type);

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174';

let pdfJsLoaderPromise = null;

const loadPdfJs = () => {
  if (window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }
  if (!pdfJsLoaderPromise) {
    pdfJsLoaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${PDFJS_CDN}/pdf.min.js`;
      script.async = true;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => reject(new Error('Failed to load PDF viewer'));
      document.head.appendChild(script);
    });
  }
  return pdfJsLoaderPromise;
};

const EsignWizardPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id;

  const step = Math.min(4, Math.max(1, parseInt(searchParams.get('step') || '1', 10)));

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState({ revamps: [], generated: [] });
  const [request, setRequest] = useState(null);

  const [title, setTitle] = useState('Contract for signature');
  const [sourceTab, setSourceTab] = useState('library');
  const [selectedSource, setSelectedSource] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const [signers, setSigners] = useState([emptySigner()]);
  const [fields, setFields] = useState([]);
  const [activeSignerId, setActiveSignerId] = useState(null);
  const [selectedFieldType, setSelectedFieldType] = useState('signature');
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState('');
  const [placementMode, setPlacementMode] = useState(false);
  const [addFieldMode, setAddFieldMode] = useState(false);
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageRenderSize, setPageRenderSize] = useState({ width: 0, height: 0 });
  const [pdfZoom, setPdfZoom] = useState(1);

  const pdfOverlayRef = useRef(null);
  const pageWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const setStep = (n) => setSearchParams({ step: String(n) });

  const loadSources = useCallback(async () => {
    try {
      const data = await apiRequest('esign/document-sources?kind=contract');
      setSources({ revamps: data.revamps || [], generated: data.generated || [] });
    } catch (err) {
      toast.error(err.message || 'Failed to load document library');
    }
  }, []);

  const loadRequest = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await apiRequest(`esign/requests/${id}`);
      setRequest(data);
      setTitle(data.title || '');
      setMessage(data.message || '');
      setSigners(
        (data.signers || []).length
          ? data.signers.map((s) => ({
              ...s,
              access_code: s.access_code || '',
              private_message: s.private_message || '',
            }))
          : [emptySigner()]
      );
      setFields(data.fields || []);
      if (data.signers?.length) setActiveSignerId(data.signers[0].id);
    } catch (err) {
      toast.error(err.message || 'Failed to load request');
      navigate('/esign');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isNew) loadSources();
    else loadRequest();
  }, [isNew, loadSources, loadRequest]);

  useEffect(() => {
    if (request?.signers?.length && !activeSignerId) {
      setActiveSignerId(request.signers[0].id);
    }
  }, [request, activeSignerId]);

  useEffect(() => {
    if (step === 3) {
      setPlacementMode(false);
      setAddFieldMode(false);
    }
  }, [step]);

  useEffect(() => {
    if (step !== 3 || !request?.resolved_pdf_url) {
      setPdfDoc(null);
      return undefined;
    }
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const token = localStorage.getItem('docxsai-token');
        const res = await fetch(request.resolved_pdf_url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Failed to fetch PDF');
        const data = await res.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data });
        const pdf = await loadingTask.promise;
        if (!cancelled) setPdfDoc(pdf);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to load PDF preview');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, request?.resolved_pdf_url]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || step !== 3) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth = pdfOverlayRef.current?.clientWidth || baseViewport.width;
        const fitScale = Math.min(1.5, Math.max(0.5, (containerWidth - 32) / baseViewport.width));
        const scale = fitScale * pdfZoom;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) {
          setPageRenderSize({ width: viewport.width, height: viewport.height });
        }
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to render page');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage, pdfZoom, step]);

  const updateFieldByKey = useCallback((key, patch) => {
    setFields((prev) =>
      prev.map((field, index) => (fieldKey(field, index) === key ? { ...field, ...patch } : field))
    );
  }, []);

  useEffect(() => {
    const handleMove = (event) => {
      if (!dragRef.current || !pageWrapRef.current) return;
      const { key, startX, startY, origX, origY, widthPct, heightPct } = dragRef.current;
      const rect = pageWrapRef.current.getBoundingClientRect();
      const dx = ((event.clientX - startX) / rect.width) * 100;
      const dy = ((event.clientY - startY) / rect.height) * 100;
      updateFieldByKey(key, {
        x_pct: Math.max(0, Math.min(100 - widthPct, origX + dx)),
        y_pct: Math.max(0, Math.min(100 - heightPct, origY + dy)),
      });
    };
    const handleUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [updateFieldByKey]);

  const libraryItems = useMemo(
    () => [...(sources.revamps || []), ...(sources.generated || [])],
    [sources]
  );

  const createDraft = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      form.append('title', title.trim() || 'Untitled');
      if (sourceTab === 'upload') {
        if (!uploadFile) throw new Error('Select a PDF or DOCX file');
        form.append('source_type', 'upload');
        form.append('file', uploadFile);
      } else {
        if (!selectedSource) throw new Error('Select a contract from your library');
        form.append('source_type', selectedSource.source_type);
        if (selectedSource.source_type === 'revamp') {
          form.append('source_revamp_id', String(selectedSource.source_id));
        } else {
          form.append('source_generation_id', String(selectedSource.source_id));
        }
      }

      const token = localStorage.getItem('docxsai-token');
      const res = await fetch(`${API_BASE}/esign/requests`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create draft');
      toast.success('Document ready');
      navigate(`/esign/${data.id}?step=2`);
    } catch (err) {
      toast.error(err.message || 'Failed to create draft');
    } finally {
      setSaving(false);
    }
  };

  const saveSigners = async () => {
    const valid = signers.filter((s) => s.name.trim() && s.email.trim());
    if (!valid.length) {
      toast.error('Add at least one signer with name and email');
      return false;
    }
    const missingCode = valid.find((s) => !s.access_code?.trim() || s.access_code.trim().length < 4);
    if (missingCode) {
      toast.error('Each signer needs an access code (at least 4 characters)');
      return false;
    }
    setSaving(true);
    try {
      const data = await apiRequest(`esign/requests/${id}/signers`, {
        method: 'PUT',
        body: valid.map((s, i) => ({
          name: s.name.trim(),
          email: s.email.trim(),
          role: s.role || 'signer',
          signing_order: i,
          is_mandatory: s.is_mandatory !== false,
          access_code: s.access_code.trim(),
          private_message: s.private_message?.trim() || null,
        })),
      });
      setRequest(data);
      setSigners(data.signers || valid);
      if (data.signers?.[0]?.id) setActiveSignerId(data.signers[0].id);
      return true;
    } catch (err) {
      toast.error(err.message || 'Failed to save signers');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const payload = fields.map((f) => ({
        signer_id: f.signer_id,
        field_type: f.field_type,
        page: f.page,
        x_pct: f.x_pct,
        y_pct: f.y_pct,
        width_pct: f.width_pct,
        height_pct: f.height_pct,
        required: f.required !== false,
        label: f.label || null,
        font_size: f.font_size || 11,
      }));
      const data = await apiRequest(`esign/requests/${id}/fields`, {
        method: 'PUT',
        body: payload,
      });
      setRequest(data);
      setFields(data.fields || payload);
      return true;
    } catch (err) {
      toast.error(err.message || 'Failed to save fields');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const sendEnvelope = async () => {
    setSaving(true);
    try {
      await apiRequest(`esign/requests/${id}`, {
        method: 'PATCH',
        body: { message: message.trim() || null },
      });
      await apiRequest(`esign/requests/${id}/send`, { method: 'POST' });
      toast.success('Sent for signature');
      navigate('/esign');
    } catch (err) {
      toast.error(err.message || 'Failed to send');
    } finally {
      setSaving(false);
    }
  };

  const signNowAndFinish = async () => {
    setSaving(true);
    try {
      const fieldsOk = await saveFields();
      if (!fieldsOk) return;
      await apiRequest(`esign/requests/${id}`, {
        method: 'PATCH',
        body: { message: message.trim() || null },
      });
      const data = await apiRequest(`esign/requests/${id}/send?self_sign_only=true`, { method: 'POST' });
      if (data.my_sign_url) {
        window.location.href = data.my_sign_url;
        return;
      }
      toast.error('Add yourself as a signer using the same email as your DocxsAi account.');
    } catch (err) {
      toast.error(err.message || 'Failed to start signing');
    } finally {
      setSaving(false);
    }
  };

  const addFieldAtClick = (event) => {
    if (!activeSignerId || !pageWrapRef.current || !placementMode || !addFieldMode) return;
    event.stopPropagation();
    const rect = pageWrapRef.current.getBoundingClientRect();
    const xPct = ((event.clientX - rect.left) / rect.width) * 100;
    const yPct = ((event.clientY - rect.top) / rect.height) * 100;
    const defaults = { signature: [22, 8], initial: [12, 5], checkbox: [4, 4], text: [20, 5] };
    const [w, h] = defaults[selectedFieldType] || [18, 5];
    const tempId = Date.now();
    setFields((prev) => [
      ...prev,
      {
        tempId,
        signer_id: activeSignerId,
        field_type: selectedFieldType,
        page: currentPage,
        x_pct: Math.max(0, Math.min(100 - w, xPct - w / 2)),
        y_pct: Math.max(0, Math.min(100 - h, yPct - h / 2)),
        width_pct: w,
        height_pct: h,
        required: true,
        label: selectedFieldType,
        font_size: 11,
      },
    ]);
    setSelectedFieldKey(String(tempId));
    setAddFieldMode(false);
  };

  const startFieldDrag = (event, field, index) => {
    event.stopPropagation();
    event.preventDefault();
    if (!placementMode) return;
    const key = fieldKey(field, index);
    setSelectedFieldKey(key);
    setAddFieldMode(false);
    dragRef.current = {
      key,
      startX: event.clientX,
      startY: event.clientY,
      origX: field.x_pct,
      origY: field.y_pct,
      widthPct: field.width_pct,
      heightPct: field.height_pct,
    };
  };

  const handlePageBackgroundClick = (event) => {
    if (placementMode && addFieldMode) {
      addFieldAtClick(event);
      return;
    }
    if (placementMode) {
      setSelectedFieldKey(null);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      if (!isNew && request?.resolved_pdf_url) {
        const nextStep = getResumeStep(request);
        setStep(nextStep > 1 ? nextStep : 2);
        return;
      }
      await createDraft();
      return;
    }
    if (step === 2) {
      const ok = await saveSigners();
      if (ok) setStep(3);
      return;
    }
    if (step === 3) {
      const ok = await saveFields();
      if (ok) setStep(4);
      return;
    }
    if (step === 4) {
      await sendEnvelope();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24 text-[var(--color-text-muted)]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const pdfUrl = request?.resolved_pdf_url;
  const pageCount = pdfDoc?.numPages || request?.page_dimensions?.length || 1;
  const pageFieldsWithIndex = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => field.page === currentPage);
  const selectedFieldEntry = (() => {
    if (!selectedFieldKey) return null;
    const index = fields.findIndex((field, fieldIndex) => fieldKey(field, fieldIndex) === selectedFieldKey);
    if (index < 0) return null;
    return { field: fields[index], index, key: selectedFieldKey };
  })();
  const canSignNow = Boolean(
    user?.email &&
      (request?.signers || signers).some(
        (signer) => signer.email?.toLowerCase().trim() === user.email.toLowerCase().trim()
      )
  );

  return (
    <div className="p-6 max-w-5xl mx-auto w-full">
      <Link to="/esign" className="inline-flex items-center gap-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to eSign
      </Link>

      <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">
        {isNew ? 'New E-Signature' : request?.title || 'Edit envelope'}
      </h1>

      <div className="flex flex-wrap gap-2 mb-8">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div
              key={label}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                active ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : done ? 'bg-green-900/40 text-green-300' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]'
              }`}
            >
              {done ? <CheckCircle2 className="w-4 h-4" /> : <span>{n}</span>}
              {label}
            </div>
          );
        })}
      </div>

      {step === 1 && isNew && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm text-[var(--color-text-muted)] mb-1">Envelope title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)] text-[var(--color-text)] px-4 py-2"
            />
          </div>

          <div className="flex gap-2">
            {['library', 'upload'].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setSourceTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm ${
                  sourceTab === tab ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
                }`}
              >
                {tab === 'library' ? 'From my library' : 'Upload new file'}
              </button>
            ))}
          </div>

          {sourceTab === 'library' ? (
            <div className="grid gap-3 max-h-96 overflow-y-auto">
              {libraryItems.length === 0 ? (
                <p className="text-[var(--color-text-muted)] text-sm">No contract documents in your library yet. Revamp or generate a contract first.</p>
              ) : (
                libraryItems.map((item) => (
                  <button
                    key={`${item.source_type}-${item.source_id}`}
                    type="button"
                    onClick={() => setSelectedSource(item)}
                    className={`text-left p-4 rounded-lg border ${
                      selectedSource?.source_id === item.source_id && selectedSource?.source_type === item.source_type
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                        : 'border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 hover:border-[var(--color-border)]'
                    }`}
                  >
                    <p className="text-[var(--color-text)] font-medium">{item.title}</p>
                    <p className="text-xs text-[var(--color-text-muted)] capitalize mt-1">{item.source_type}</p>
                  </button>
                ))
              )}
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[var(--color-border)] rounded-xl p-10 cursor-pointer hover:border-[var(--color-primary)]">
              <Upload className="w-10 h-10 text-[var(--color-text-muted)] mb-2" />
              <span className="text-[var(--color-text-secondary)]">{uploadFile ? uploadFile.name : 'PDF or DOCX (max 10MB)'}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </label>
          )}
        </div>
      )}

      {step === 1 && !isNew && request && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-[var(--color-surface-muted)]/50 border border-[var(--color-border)]">
            <p className="text-green-400 text-sm mb-1">Document attached</p>
            <p className="text-[var(--color-text)] font-medium">{request.original_filename || request.title}</p>
            <p className="text-xs text-[var(--color-text-muted)] capitalize mt-1">Source: {request.source_type}</p>
          </div>
          {pdfUrl && (
            <iframe
              title="Attached document"
              src={pdfUrl}
              className="w-full h-[60vh] min-h-[400px] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]"
            />
          )}
          <p className="text-sm text-[var(--color-text-muted)]">Click Continue to pick up where you left off.</p>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {signers.map((signer, index) => (
            <div key={index} className="p-4 rounded-lg bg-[var(--color-surface-muted)]/50 border border-[var(--color-border)] space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--color-text-muted)]">Signer {index + 1}</span>
                {signers.length > 1 && (
                  <button type="button" onClick={() => setSigners(signers.filter((_, i) => i !== index))} className="text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  placeholder="Full name"
                  value={signer.name}
                  onChange={(e) => {
                    const next = [...signers];
                    next[index].name = e.target.value;
                    setSigners(next);
                  }}
                  className="rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] px-3 py-2"
                />
                <input
                  placeholder="Email"
                  type="email"
                  value={signer.email}
                  onChange={(e) => {
                    const next = [...signers];
                    next[index].email = e.target.value;
                    setSigners(next);
                  }}
                  className="rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] px-3 py-2"
                />
              </div>
              <input
                placeholder="Access code (required, min 4 characters)"
                value={signer.access_code}
                onChange={(e) => {
                  const next = [...signers];
                  next[index].access_code = e.target.value;
                  setSigners(next);
                }}
                className="w-full rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] px-3 py-2 text-sm"
                required
                minLength={4}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => setSigners([...signers, emptySigner()])}
            className="text-[var(--color-primary)] text-sm hover:text-[var(--color-primary)]"
          >
            + Add another signer
          </button>
          {user && (
            <button
              type="button"
              onClick={() =>
                setSigners([
                  {
                    ...emptySigner(),
                    name: user.full_name || '',
                    email: user.email || '',
                  },
                ])
              }
              className="block text-sm text-blue-400 hover:text-blue-300"
            >
              I&apos;m signing this myself only (use my account)
            </button>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] disabled:opacity-40"
              >
                Prev
              </button>
              <span className="text-[var(--color-text-muted)] text-sm">
                Page {currentPage} / {pageCount}
              </span>
              <button
                type="button"
                disabled={currentPage >= pageCount}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] disabled:opacity-40"
              >
                Next
              </button>
              <span className="text-gray-600 hidden sm:inline">|</span>
              <button
                type="button"
                onClick={() => {
                  setPlacementMode(false);
                  setAddFieldMode(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${
                  !placementMode ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'
                }`}
              >
                <Hand className="w-4 h-4" />
                Scroll &amp; view
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlacementMode(true);
                  setAddFieldMode(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${
                  placementMode ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'
                }`}
              >
                <MousePointerClick className="w-4 h-4" />
                Place fields
              </button>
              <button
                type="button"
                onClick={() => setPdfZoom((z) => Math.max(0.6, z - 0.1))}
                className="p-1.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
                title="Zoom out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-[var(--color-text-muted)] w-10 text-center">{Math.round(pdfZoom * 100)}%</span>
              <button
                type="button"
                onClick={() => setPdfZoom((z) => Math.min(1.8, z + 0.1))}
                className="p-1.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
                title="Zoom in"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
            <div
              ref={pdfOverlayRef}
              className={`w-full h-[75vh] min-h-[520px] bg-[var(--color-surface)] rounded-lg overflow-auto border ${
                placementMode && addFieldMode
                  ? 'border-[var(--color-primary)] cursor-crosshair'
                  : placementMode
                    ? 'border-[var(--color-primary)]'
                    : 'border-[var(--color-border)]'
              }`}
            >
              <div className="flex justify-center p-4 min-h-full">
                {pdfLoading && (
                  <div className="flex items-center justify-center text-[var(--color-text-muted)] py-24">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                )}
                {!pdfLoading && pdfUrl && (
                  <div
                    ref={pageWrapRef}
                    className="relative bg-white shadow-lg shrink-0"
                    style={{
                      width: pageRenderSize.width || '100%',
                      height: pageRenderSize.height || 'auto',
                      minHeight: pageRenderSize.height ? undefined : 400,
                    }}
                    onClick={handlePageBackgroundClick}
                  >
                    <canvas ref={canvasRef} className="block w-full h-auto" />
                    {pageFieldsWithIndex.map(({ field: f, index: globalIndex }) => {
                      const key = fieldKey(f, globalIndex);
                      const isSelected = selectedFieldKey === key;
                      return (
                        <div
                          key={key}
                          role="button"
                          tabIndex={0}
                          onMouseDown={(event) => startFieldDrag(event, f, globalIndex)}
                          onClick={(event) => event.stopPropagation()}
                          className={`absolute border-2 bg-white/95 text-gray-900 px-1 flex items-center overflow-hidden ${
                            isSelected ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-ring)]' : 'border-[var(--color-primary)]'
                          } ${placementMode ? 'pointer-events-auto cursor-move' : 'pointer-events-none'}`}
                          style={{
                            left: `${f.x_pct}%`,
                            top: `${f.y_pct}%`,
                            width: `${f.width_pct}%`,
                            height: `${f.height_pct}%`,
                            fontSize: `${Math.max(9, Math.min(14, (f.font_size || 11) * 0.75))}px`,
                          }}
                        >
                          <span className="capitalize truncate shrink-0">{f.field_type}:</span>
                          {showsUnderscore(f.field_type) && (
                            <span className="text-[var(--color-text-muted)] ml-1 flex-1 border-b border-gray-400 min-w-[24px]" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              {placementMode
                ? addFieldMode
                  ? `Adding ${selectedFieldType} fields: click on the page to place one. Drag existing fields to reposition without duplicating.`
                  : 'Place fields mode: drag fields to reposition, or use + Add field to place a new one. Select a field in the list to adjust size and font.'
                : 'Scroll & view mode: scroll and zoom the document. Field markers move with the page. Switch to Place fields to edit.'}
            </p>
            {fields.length === 0 && (
              <p className="text-xs text-[var(--color-primary)]/90 mt-2">
                No fields placed? Signers can still fill printed lines directly on the document using Add text and Add
                signature when they open their signing link.
              </p>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[var(--color-text-muted)] block mb-1">Assign fields to</label>
              <select
                value={activeSignerId || ''}
                onChange={(e) => setActiveSignerId(Number(e.target.value))}
                className="w-full rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)] text-[var(--color-text)] px-3 py-2"
              >
                {(request?.signers || signers).map((s) => (
                  <option key={s.id || s.email} value={s.id}>
                    {s.name || s.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-[var(--color-text-muted)] block mb-1">Field type</label>
              <div className="flex flex-wrap gap-2">
                {FIELD_TYPES.map((ft) => (
                  <button
                    key={ft.id}
                    type="button"
                    onClick={() => {
                      setSelectedFieldType(ft.id);
                      setPlacementMode(true);
                      setAddFieldMode(false);
                    }}
                    className={`px-2 py-1 rounded text-xs ${
                      selectedFieldType === ft.id ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {ft.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={!activeSignerId}
                onClick={() => {
                  setPlacementMode(true);
                  setAddFieldMode(true);
                  setSelectedFieldKey(null);
                }}
                className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  addFieldMode
                    ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)] ring-2 ring-[var(--color-ring)]'
                    : 'bg-[var(--color-surface-muted)] text-[var(--color-primary)] border border-[var(--color-primary)]/50 hover:bg-[var(--color-surface-muted)]'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add {selectedFieldType} field
              </button>
              {addFieldMode && (
                <p className="text-xs text-[var(--color-primary)] mt-2">
                  Click on the document where you want the field. Drag existing fields without duplicating them.
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-[var(--color-text-muted)] mb-2">Placed fields ({fields.length})</p>
              <ul className="space-y-1 max-h-40 overflow-y-auto text-xs">
                {fields.map((f, i) => (
                  <li key={fieldKey(f, i)} className="flex justify-between text-[var(--color-text-secondary)]">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFieldKey(fieldKey(f, i));
                        setCurrentPage(f.page);
                        setPlacementMode(true);
                        setAddFieldMode(false);
                      }}
                      className={`text-left truncate ${selectedFieldKey === fieldKey(f, i) ? 'text-[var(--color-primary)]' : ''}`}
                    >
                      p{f.page} {f.field_type}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFields(fields.filter((_, j) => j !== i));
                        if (selectedFieldKey === fieldKey(f, i)) setSelectedFieldKey(null);
                      }}
                      className="text-red-400 shrink-0 ml-2"
                    >
                      remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {selectedFieldEntry && (
              <div className="p-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] space-y-3">
                <p className="text-sm text-[var(--color-text)] font-medium capitalize">
                  Edit {selectedFieldEntry.field.field_type} field
                </p>
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">Font size ({selectedFieldEntry.field.font_size || 11}px)</label>
                  <input
                    type="range"
                    min="8"
                    max="24"
                    value={selectedFieldEntry.field.font_size || 11}
                    onChange={(e) =>
                      updateFieldByKey(selectedFieldEntry.key, { font_size: Number(e.target.value) })
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">Width ({Math.round(selectedFieldEntry.field.width_pct)}%)</label>
                  <input
                    type="range"
                    min="5"
                    max="60"
                    value={selectedFieldEntry.field.width_pct}
                    onChange={(e) =>
                      updateFieldByKey(selectedFieldEntry.key, { width_pct: Number(e.target.value) })
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">Height ({Math.round(selectedFieldEntry.field.height_pct)}%)</label>
                  <input
                    type="range"
                    min="3"
                    max="20"
                    value={selectedFieldEntry.field.height_pct}
                    onChange={(e) =>
                      updateFieldByKey(selectedFieldEntry.key, { height_pct: Number(e.target.value) })
                    }
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4 max-w-xl">
          <p className="text-[var(--color-text-secondary)]">
            <strong className="text-[var(--color-text)]">{request?.title}</strong> — {request?.signers?.length || 0} signers,{' '}
            {fields.length} fields
          </p>
          <div className="p-4 rounded-lg bg-[var(--color-surface-muted)]/50 border border-[var(--color-border)] text-sm text-[var(--color-text-muted)]">
            <p className="text-[var(--color-text-secondary)] font-medium mb-1">Signing a document that already has printed lines?</p>
            <p>
              Place your name, date, and signature fields on top of the blank lines in step 3, then use{' '}
              <strong className="text-[var(--color-text)]">Sign now &amp; download</strong> if you are the only signer left to sign.
            </p>
          </div>
          <div>
            <label className="block text-sm text-[var(--color-text-muted)] mb-1">Message to signers (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)] text-[var(--color-text)] px-4 py-2"
            />
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Signers will receive an email with a secure link. They do not need a DocxsAi account.
          </p>
        </div>
      )}

      {!isNew && request?.status === 'completed' && (
        <div className="mt-6 p-4 rounded-lg bg-green-900/20 border border-green-700">
          <p className="text-green-300 mb-2">This envelope is complete.</p>
          {request.completed_pdf_url && (
            <a href={request.completed_pdf_url} target="_blank" rel="noopener noreferrer" className="text-[var(--color-primary)] underline">
              Download signed PDF
            </a>
          )}
        </div>
      )}

      {(!request || request.status === 'draft') && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-10 pt-6 border-t border-gray-800">
          <button
            type="button"
            disabled={step <= 1}
            onClick={() => setStep(step - 1)}
            className="px-4 py-2 rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] disabled:opacity-40"
          >
            Back
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {step === 4 && canSignNow && (
              <button
                type="button"
                disabled={saving}
                onClick={signNowAndFinish}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-primary)] text-[var(--color-primary)] font-medium disabled:opacity-60"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign now &amp; download
              </button>
            )}
            <button
              type="button"
              disabled={saving}
              onClick={goNext}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg brand-gradient-btn font-medium disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {step === 4 ? 'Send for signature' : 'Continue'}
              {step < 4 && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EsignWizardPage;
